import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  XCircle,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";

import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { logOut } from "@/store/actions/authActions";
import { OtpInput } from "@/components/shared/OtpInput";

const OTP_LENGTH = 6;
const OTP_COOLDOWN_SECONDS = 60;

const calculateStrength = (value: string) => {
  let score = 0;

  if (value.length >= 8) score++;
  if (/[A-Z]/.test(value)) score++;
  if (/[a-z]/.test(value)) score++;
  if (/\d/.test(value)) score++;
  if (/[^A-Za-z0-9]/.test(value)) score++;

  return score;
};

const getStrengthLabel = (score: number) => {
  if (score <= 1) return "Very Weak";
  if (score === 2) return "Weak";
  if (score === 3) return "Fair";
  if (score === 4) return "Strong";

  return "Very Strong";
};

const getRequirements = (value: string) => [
  {
    label: "At least 8 characters",
    valid: value.length >= 8,
  },
  {
    label: "One uppercase letter",
    valid: /[A-Z]/.test(value),
  },
  {
    label: "One lowercase letter",
    valid: /[a-z]/.test(value),
  },
  {
    label: "One number",
    valid: /\d/.test(value),
  },
  {
    label: "One special character",
    valid: /[^A-Za-z0-9]/.test(value),
  },
];

const PasswordField = ({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>

      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          value={value}
          placeholder={placeholder}
          autoComplete="new-password"
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-14 rounded-xl border border-border bg-background px-4 pr-12 outline-none transition focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />

        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label={
            visible ? "Hide transaction password" : "Show transaction password"
          }
        >
          {visible ? (
            <EyeOff className="h-5 w-5" />
          ) : (
            <Eye className="h-5 w-5" />
          )}
        </button>
      </div>
    </div>
  );
};

export default function TransactionPasswordResetPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.auth);

  const email = user?.email ?? "";

  const [step, setStep] = useState<"request" | "otp" | "password" | "success">(
    "request",
  );

  const [password, setPassword] = useState("");

  const [confirmPassword, setConfirmPassword] = useState("");

  const [isResetLoading, setIsResetLoading] = useState(false);

  const strength = useMemo(() => calculateStrength(password), [password]);

  const requirements = useMemo(() => getRequirements(password), [password]);

  const strong = requirements.every((item) => item.valid);

  const passwordsMatch = password.length > 0 && password === confirmPassword;

  const canReset = strong && passwordsMatch && !isResetLoading;

  useEffect(() => {
    if (!user) {
      navigate("/login", {
        replace: true,
      });
    }
  }, [user, navigate]);

  const handleRequestOtp = async () => {
    if (!email) {
      toast.error("No registered email address was found.");
    }

    const response = await makeRequest.put("/user/forgot-security-password");

    const data = response?.data;

    return {
      resendCooldownSeconds:
        data?.otp?.resendCooldownSeconds ?? OTP_COOLDOWN_SECONDS,

      message:
        data?.message || "A verification code has been sent to your email.",
    };
  };

  const handleVerifyOtp = async (otp: string) => {
    const response = await makeRequest.put("/auth/otp/verify", {
      otp,
    });

    console.log("VERIFY OTP RESPONSE", response);

    const data = response?.data;

    const isVerified = data?.result?.success ?? false;

    if (!isVerified) {
      throw new Error(data?.message || "One Time Password not verified.");
    }

    setStep("password");

    toast.success(data?.message || "OTP verified successfully.");
  };

  const handleResetPassword = async () => {
    if (!canReset) {
      return;
    }

    try {
      setIsResetLoading(true);

      const response = await makeRequest.put("/user/security-code/create", {
        securityCode: password,
        isReset: true,
      });

      toast.success(
        response?.data?.message || "Transaction password reset successfully.",
      );

      dispatch(logOut());

      setPassword("");
      setConfirmPassword("");

      setStep("success");

      setTimeout(() => {
        navigate("/login", {
          replace: true,
        });
      }, 1500);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsResetLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 ring-1 ring-emerald-500/20">
            <CheckCircle2 className="h-9 w-9 text-emerald-500" />
          </div>

          <h1 className="text-2xl font-bold">Transaction Password Reset</h1>

          <p className="mt-3 text-sm text-muted-foreground">
            Your transaction password has been successfully reset. You have been
            logged out for your security.
          </p>

          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Redirecting to login...
          </div>
        </div>
      </div>
    );
  }

  /* =======================================================
     REQUEST
  ======================================================= */

  if (step === "request") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg">
          {/* Header */}

          <div className="text-center mb-8">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/20">
              <ShieldCheck className="h-9 w-9 text-primary" />
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Reset Transaction Password
            </h1>

            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
              We will send a verification code to your registered email address
              before you can create a new transaction password.
            </p>
          </div>

          {/* Card */}

          <div className="rounded-2xl border border-border/60 bg-card p-5 sm:p-7 shadow-xl shadow-black/5">
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 mb-6">
              <div className="flex gap-3">
                <ShieldCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />

                <div>
                  <p className="text-sm font-semibold">Verify your identity</p>

                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    A 6-digit verification code will be sent to:
                  </p>

                  <p className="mt-2 text-sm font-semibold break-all">
                    {email}
                  </p>
                </div>
              </div>
            </div>

            <Button
              type="button"
              onClick={async () => {
                try {
                  await handleRequestOtp();

                  setStep("otp");
                } catch (error) {
                  toast.error(getErrorMessage(error));
                }
              }}
              className="w-full h-14 rounded-xl font-semibold"
            >
              Send OTP
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mt-5 w-full text-sm text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            <span>Your transaction security is protected</span>
          </div>
        </div>
      </div>
    );
  }

  if (step === "otp") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full sm:max-w-md mx-auto">
          {/* Header */}

          <div className="text-center mb-8">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/20">
              <ShieldCheck className="h-9 w-9 text-primary" />
            </div>

            <h1 className="text-2xl font-bold">Verify Your Identity</h1>

            <p className="mt-2 text-sm text-muted-foreground">
              Enter the 6-digit code sent to
            </p>

            <p className="mt-1 text-sm font-semibold break-all">{email}</p>
          </div>

          {/* Card */}

          <div className="rounded-2xl border border-border/60 bg-card p-6 sm:p-8 shadow-xl shadow-black/5">
            <OtpInput
              length={OTP_LENGTH}
              cooldown={OTP_COOLDOWN_SECONDS}
              onRequestOtp={handleRequestOtp}
              onVerifyOtp={handleVerifyOtp}
              requestLabel="Send OTP"
              resendLabel="Resend OTP"
              verifyLabel="Verify OTP"
            />

            {/* Back */}

            <button
              type="button"
              onClick={() => setStep("request")}
              className="mt-6 w-full text-sm text-muted-foreground hover:text-foreground"
            >
              Use a different verification method
            </button>
          </div>

          <div className="mt-6 text-center text-xs text-muted-foreground">
            Never share this verification code with anyone.
          </div>
        </div>
      </div>
    );
  }

  /* =======================================================
     NEW PASSWORD
  ======================================================= */

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        {/* Header */}

        <div className="text-center mb-8">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/20">
            <ShieldCheck className="h-9 w-9 text-primary" />
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Create New Transaction Password
          </h1>

          <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
            Your identity has been verified. Create a new transaction password
            below.
          </p>
        </div>

        {/* Card */}

        <div className="rounded-2xl border border-border/60 bg-card p-5 sm:p-7 shadow-xl shadow-black/5">
          <div className="mb-6 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
            <div className="flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />

              <div>
                <p className="text-sm font-semibold">Identity verified</p>

                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  You can now create a new transaction password. For your
                  protection, all active sessions will be signed out after the
                  password is changed.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            {/* Password */}

            <PasswordField
              label="New Transaction Password"
              value={password}
              onChange={setPassword}
              placeholder="Create a new transaction password"
            />

            {/* Strength */}

            {password.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Password strength
                  </span>

                  <span className="text-xs font-semibold">
                    {getStrengthLabel(strength)}
                  </span>
                </div>

                <Progress value={(strength / 5) * 100} className="h-2" />

                <div className="grid gap-2">
                  {requirements.map((requirement) => (
                    <div
                      key={requirement.label}
                      className="flex items-center gap-2 text-xs"
                    >
                      {requirement.valid ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-destructive" />
                      )}

                      <span
                        className={
                          requirement.valid
                            ? "text-foreground"
                            : "text-muted-foreground"
                        }
                      >
                        {requirement.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirm */}

            {strong && (
              <PasswordField
                label="Confirm Transaction Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="Confirm your new transaction password"
              />
            )}

            {confirmPassword && !passwordsMatch && (
              <p className="text-xs text-destructive">
                Transaction passwords do not match.
              </p>
            )}

            {/* Reset */}

            <Button
              type="button"
              disabled={!canReset}
              onClick={handleResetPassword}
              className="w-full h-14 rounded-xl font-semibold"
            >
              {isResetLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting password...
                </>
              ) : (
                <>
                  Reset Transaction Password
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>

            <p className="text-center text-[11px] leading-5 text-muted-foreground">
              After resetting your transaction password, you will be logged out
              and required to sign in again.
            </p>
          </div>
        </div>

        {/* Security indicator */}

        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4 text-emerald-500" />

          <span>Your transaction security is protected</span>
        </div>
      </div>
    </div>
  );
}
